import React, { useState, useRef, useCallback } from 'react';
import { FiX } from 'react-icons/fi';
import { useLayout, LAYOUTS } from '../context/LayoutContext';
import MiniChat from './MiniChat';
import styles from '../styles/quadrantLayout.module.css';

const QuadrantLayout = ({ 
  documents = [], 
  sidebarCollapsed, 
  onUploadDocument,
  onCloseAll 
}) => {
  const { layout, resetLayout } = useLayout();
  const [panels, setPanels] = useState(() => {
    // Initialize panels based on layout
    const count = getPanelCount(layout);
    return Array.from({ length: count }, (_, i) => ({
      id: i + 1,
      visible: true,
    }));
  });
  
  const [draggedPanel, setDraggedPanel] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  // For quad layout, we need separate sizes for each divider
  const [sizes, setSizes] = useState({ 
    horizontal: 50, 
    vertical: 50,
    // For quad layout individual control
    horizontalTop: 50,
    horizontalBottom: 50,
    verticalLeft: 50,
    verticalRight: 50
  });
  const containerRef = useRef(null);
  const resizingRef = useRef(null);

  function getPanelCount(layoutType) {
    switch (layoutType) {
      case LAYOUTS.SPLIT_H:
      case LAYOUTS.SPLIT_V:
        return 2;
      case LAYOUTS.TRIPLE_L:
      case LAYOUTS.TRIPLE_R:
      case LAYOUTS.TRIPLE_T:
      case LAYOUTS.TRIPLE_B:
        return 3;
      case LAYOUTS.QUAD:
        return 4;
      default:
        return 1;
    }
  }

  // Update panels when layout changes
  React.useEffect(() => {
    const count = getPanelCount(layout);
    setPanels(Array.from({ length: count }, (_, i) => ({
      id: i + 1,
      visible: true,
    })));
    setSizes({ 
      horizontal: 50, 
      vertical: 50,
      horizontalTop: 50,
      horizontalBottom: 50,
      verticalLeft: 50,
      verticalRight: 50
    });
  }, [layout]);

  // Handle drag start
  const handleDragStart = useCallback((panelId) => {
    setDraggedPanel(panelId);
  }, []);

  // Handle drag over
  const handleDragOver = useCallback((e, panelId) => {
    e.preventDefault();
    if (draggedPanel && draggedPanel !== panelId) {
      setDropTarget(panelId);
    }
  }, [draggedPanel]);

  // Handle drop - swap panels
  const handleDrop = useCallback((e, targetPanelId) => {
    e.preventDefault();
    if (draggedPanel && draggedPanel !== targetPanelId) {
      setPanels(prev => {
        const newPanels = [...prev];
        const draggedIdx = newPanels.findIndex(p => p.id === draggedPanel);
        const targetIdx = newPanels.findIndex(p => p.id === targetPanelId);
        
        if (draggedIdx !== -1 && targetIdx !== -1) {
          
          [newPanels[draggedIdx], newPanels[targetIdx]] = [newPanels[targetIdx], newPanels[draggedIdx]];
        }
        return newPanels;
      });
    }
    setDraggedPanel(null);
    setDropTarget(null);
  }, [draggedPanel]);

  
  const handleDragEnd = useCallback(() => {
    setDraggedPanel(null);
    setDropTarget(null);
  }, []);

  
  const handleClosePanel = useCallback((panelId) => {
    const visiblePanels = panels.filter(p => p.visible);
    if (visiblePanels.length <= 1) {
      // Last panel - close all and return to single view
      resetLayout();
      onCloseAll?.();
      return;
    }
    
    setPanels(prev => prev.map(p => 
      p.id === panelId ? { ...p, visible: false } : p
    ));
  }, [panels, resetLayout, onCloseAll]);

  // Handle close all
  const handleCloseAll = useCallback(() => {
    resetLayout();
    onCloseAll?.();
  }, [resetLayout, onCloseAll]);

  // Handle resize - direction can be 'horizontal', 'vertical', 'verticalLeft', 'verticalRight'
  const handleResizeStart = useCallback((direction, e) => {
    e.preventDefault();
    resizingRef.current = direction;
    
    const handleMouseMove = (moveEvent) => {
      if (!containerRef.current || !resizingRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const dir = resizingRef.current;
      
      if (dir === 'horizontal') {
        // For TRIPLE_R layout, the right panel is on the right side, so we need to calculate from right
        const newSize = ((moveEvent.clientX - rect.left) / rect.width) * 100;
        setSizes(prev => ({ ...prev, horizontal: Math.min(80, Math.max(20, newSize)) }));
      } else if (dir === 'horizontal-right') {
        // For right-side layouts, invert the calculation
        const newSize = ((rect.right - moveEvent.clientX) / rect.width) * 100;
        setSizes(prev => ({ ...prev, horizontal: Math.min(80, Math.max(20, 100 - newSize)) }));
      } else if (dir === 'vertical') {
        const newSize = ((moveEvent.clientY - rect.top) / rect.height) * 100;
        setSizes(prev => ({ ...prev, vertical: Math.min(80, Math.max(20, newSize)) }));
      } else if (dir === 'verticalLeft') {
        // Left column vertical divider (for quad layout)
        const newSize = ((moveEvent.clientY - rect.top) / rect.height) * 100;
        setSizes(prev => ({ ...prev, verticalLeft: Math.min(80, Math.max(20, newSize)) }));
      } else if (dir === 'verticalRight') {
        // Right column vertical divider (for quad layout)
        const newSize = ((moveEvent.clientY - rect.top) / rect.height) * 100;
        setSizes(prev => ({ ...prev, verticalRight: Math.min(80, Math.max(20, newSize)) }));
      }
    };
    
    const handleMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  // Get grid style based on layout and sizes
  const getGridStyle = () => {
    const visiblePanels = panels.filter(p => p.visible);
    const visibleCount = visiblePanels.length;
    
    // If only one panel visible, make it full screen
    if (visibleCount === 1) {
      return {
        gridTemplateColumns: '1fr',
        gridTemplateRows: '1fr',
      };
    }
    
    switch (layout) {
      case LAYOUTS.SPLIT_H:
        return {
          gridTemplateColumns: `${sizes.horizontal}% ${100 - sizes.horizontal}%`,
          gridTemplateRows: '1fr',
        };
      case LAYOUTS.SPLIT_V:
        return {
          gridTemplateColumns: '1fr',
          gridTemplateRows: `${sizes.vertical}% ${100 - sizes.vertical}%`,
        };
      case LAYOUTS.TRIPLE_L:
        return {
          gridTemplateColumns: `${sizes.horizontal}% ${100 - sizes.horizontal}%`,
          gridTemplateRows: `${sizes.vertical}% ${100 - sizes.vertical}%`,
        };
      case LAYOUTS.TRIPLE_R:
        return {
          gridTemplateColumns: `${100 - sizes.horizontal}% ${sizes.horizontal}%`,
          gridTemplateRows: `${sizes.vertical}% ${100 - sizes.vertical}%`,
        };
      case LAYOUTS.TRIPLE_T:
        return {
          gridTemplateColumns: `${sizes.horizontal}% ${100 - sizes.horizontal}%`,
          gridTemplateRows: `${sizes.vertical}% ${100 - sizes.vertical}%`,
        };
      case LAYOUTS.TRIPLE_B:
        return {
          gridTemplateColumns: `${sizes.horizontal}% ${100 - sizes.horizontal}%`,
          gridTemplateRows: `${100 - sizes.vertical}% ${sizes.vertical}%`,
        };
      case LAYOUTS.QUAD:
        // For quad, use average of left and right vertical sizes for the row split
        const avgVertical = (sizes.verticalLeft + sizes.verticalRight) / 2;
        return {
          gridTemplateColumns: `${sizes.horizontal}% ${100 - sizes.horizontal}%`,
          gridTemplateRows: `${avgVertical}% ${100 - avgVertical}%`,
        };
      default:
        return {
          gridTemplateColumns: '1fr',
          gridTemplateRows: '1fr',
        };
    }
  };

  // Get grid area for each panel based on layout
  const getGridArea = (index, layoutType) => {
    const areas = {
      [LAYOUTS.SPLIT_H]: ['1 / 1 / 2 / 2', '1 / 2 / 2 / 3'],
      [LAYOUTS.SPLIT_V]: ['1 / 1 / 2 / 2', '2 / 1 / 3 / 2'],
      [LAYOUTS.TRIPLE_L]: ['1 / 1 / 3 / 2', '1 / 2 / 2 / 3', '2 / 2 / 3 / 3'],
      [LAYOUTS.TRIPLE_R]: ['1 / 1 / 2 / 2', '2 / 1 / 3 / 2', '1 / 2 / 3 / 3'],
      [LAYOUTS.TRIPLE_T]: ['1 / 1 / 2 / 3', '2 / 1 / 3 / 2', '2 / 2 / 3 / 3'],
      [LAYOUTS.TRIPLE_B]: ['1 / 1 / 2 / 2', '1 / 2 / 2 / 3', '2 / 1 / 3 / 3'],
      [LAYOUTS.QUAD]: ['1 / 1 / 2 / 2', '1 / 2 / 2 / 3', '2 / 1 / 3 / 2', '2 / 2 / 3 / 3'],
    };
    return areas[layoutType]?.[index] || '1 / 1 / 2 / 2';
  };

  // For single layout, don't render
  if (layout === LAYOUTS.SINGLE) {
    return null;
  }

  const visiblePanels = panels.filter(p => p.visible);
  const showHorizontalResizer = [LAYOUTS.SPLIT_H, LAYOUTS.TRIPLE_L, LAYOUTS.TRIPLE_R, LAYOUTS.TRIPLE_T, LAYOUTS.TRIPLE_B, LAYOUTS.QUAD].includes(layout) && visiblePanels.length > 1;
  const showVerticalResizer = [LAYOUTS.SPLIT_V, LAYOUTS.TRIPLE_L, LAYOUTS.TRIPLE_R, LAYOUTS.TRIPLE_T, LAYOUTS.TRIPLE_B, LAYOUTS.QUAD].includes(layout) && visiblePanels.length > 1;
  const isQuadLayout = layout === LAYOUTS.QUAD;
  const isRightLayout = layout === LAYOUTS.TRIPLE_R;

  return (
    <div 
      ref={containerRef}
      className={`${styles.quadrantContainer} ${sidebarCollapsed ? styles.sidebarCollapsed : ''}`}
      style={getGridStyle()}
    >
      {/* Close All Button */}
      <button className={styles.closeAllBtn} onClick={handleCloseAll} title="Close all panels">
        <FiX size={14} />
        <span>Close All</span>
      </button>

      {/* Panels */}
      {visiblePanels.map((panel, index) => (
        <div
          key={panel.id}
          className={`${styles.panelWrapper} ${dropTarget === panel.id ? styles.dropTarget : ''}`}
          style={{ gridArea: visiblePanels.length > 1 ? getGridArea(index, layout) : '1 / 1 / -1 / -1' }}
          onDragOver={(e) => handleDragOver(e, panel.id)}
          onDrop={(e) => handleDrop(e, panel.id)}
        >
          <MiniChat
            panelId={panel.id}
            onClose={handleClosePanel}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            isDragging={draggedPanel === panel.id}
            isDropTarget={dropTarget === panel.id}
            documents={documents}
            onUploadDocument={onUploadDocument}
          />
        </div>
      ))}

      {/* Horizontal Resizer */}
      {showHorizontalResizer && (
        <div 
          className={styles.resizerHorizontal}
          style={{ left: isRightLayout ? `${100 - sizes.horizontal}%` : `${sizes.horizontal}%` }}
          onMouseDown={(e) => handleResizeStart(isRightLayout ? 'horizontal-right' : 'horizontal', e)}
        />
      )}

      {/* Vertical Resizer - for non-quad layouts */}
      {showVerticalResizer && !isQuadLayout && (
        <div 
          className={styles.resizerVertical}
          style={{ top: `${sizes.vertical}%` }}
          onMouseDown={(e) => handleResizeStart('vertical', e)}
        />
      )}

      {/* Quad Layout - Individual Vertical Resizers */}
      {isQuadLayout && (
        <>
          {/* Left column vertical resizer */}
          <div 
            className={`${styles.resizerVertical} ${styles.resizerVerticalLeft}`}
            style={{ 
              top: `${sizes.verticalLeft}%`,
              left: 0,
              width: `${sizes.horizontal}%`
            }}
            onMouseDown={(e) => handleResizeStart('verticalLeft', e)}
          />
          {/* Right column vertical resizer */}
          <div 
            className={`${styles.resizerVertical} ${styles.resizerVerticalRight}`}
            style={{ 
              top: `${sizes.verticalRight}%`,
              left: `${sizes.horizontal}%`,
              width: `${100 - sizes.horizontal}%`
            }}
            onMouseDown={(e) => handleResizeStart('verticalRight', e)}
          />
        </>
      )}
    </div>
  );
};

export default QuadrantLayout;
